/**
 * MobileDrawer — slide-out side drawer for the Active Hand screen sidebars.
 *
 * Mobile only. The Stats and Action Panel sidebars collapse to thin vertical-
 * label strips on mobile (per wireframe `04_active_hand_mobile`). Tapping a
 * strip opens this drawer, which slides in from its edge over a backdrop
 * scrim. Tap the scrim or the close button to dismiss.
 *
 * The drawer is always mounted (not conditionally rendered) so the CSS
 * transform transition animates cleanly on open AND close. When closed, the
 * scrim has `pointer-events-none` so it doesn't intercept taps on the table.
 *
 * Hidden entirely at the `sm:` breakpoint — desktop sidebars stay expanded
 * inline and don't need drawer behavior.
 */

import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  side: 'left' | 'right';
  title: string;
  /** Optional subtitle shown under the title in the drawer header. */
  subtitle?: string;
  children: ReactNode;
}

export function MobileDrawer({
  isOpen,
  onClose,
  side,
  title,
  subtitle,
  children,
}: MobileDrawerProps) {
  return (
    <>
      {/* Scrim — fades with the drawer. pointer-events-none when closed so
          taps fall through to the table. */}
      <div
        className={cn(
          'fixed inset-0 z-30 bg-surface-overlay transition-opacity duration-300 sm:hidden',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <aside
        className={cn(
          'fixed inset-y-0 z-40 flex w-72 flex-col bg-surface-elevated shadow-xl transition-transform duration-300 sm:hidden',
          side === 'left' ? 'left-0 border-r border-border' : 'right-0 border-l border-border',
          isOpen
            ? 'translate-x-0'
            : side === 'left'
              ? '-translate-x-full'
              : 'translate-x-full',
        )}
        aria-hidden={!isOpen}
      >
        {/* Drawer header — title + close button */}
        <div className="flex shrink-0 items-start justify-between border-b border-border bg-surface-app px-3 py-2.5">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-text-secondary">
              {title}
            </div>
            {subtitle && (
              <div className="text-[9px] uppercase tracking-wider text-text-muted">
                {subtitle}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'rounded-md px-2 py-0.5 text-base text-text-muted',
              'hover:bg-surface-app hover:text-text-primary',
              'focus:outline-none focus:ring-2 focus:ring-border-focus',
            )}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">{children}</div>
      </aside>
    </>
  );
}
